import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface UndoToastProps {
  /** Chave que reinicia a contagem — ex.: id do último item registrado. */
  resetKey: string | null;
  message: string;
  durationMs?: number;
  onUndo: () => void;
  onExpire?: () => void;
}

/**
 * Toast de undo com contagem regressiva visual. Recebe uma resetKey: sempre
 * que muda, a contagem reinicia. Quando expira chama onExpire.
 */
export function UndoToast({
  resetKey,
  message,
  durationMs = 4000,
  onUndo,
  onExpire,
}: UndoToastProps) {
  const [elapsed, setElapsed] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (!resetKey) return;
    startRef.current = performance.now();
    setElapsed(0);
    const tick = (): void => {
      const e = performance.now() - startRef.current;
      if (e >= durationMs) {
        setElapsed(durationMs);
        onExpire?.();
        return;
      }
      setElapsed(e);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [resetKey, durationMs, onExpire]);

  if (!resetKey) return null;
  const pct = Math.max(0, 1 - elapsed / durationMs);
  const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsed) / 1000));

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed right-4 bottom-20 z-40 w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-md border border-(--color-border) bg-(--color-surface-elevated) shadow-lg"
    >
      <div className="flex items-center gap-3 p-3">
        <p className="flex-1 text-sm">{message}</p>
        <span className="text-xs text-(--color-fg-muted)" aria-hidden="true">
          {secondsLeft}s
        </span>
        <button
          type="button"
          onClick={onUndo}
          className={cn(
            'shrink-0 rounded-sm px-3 py-1 text-sm font-medium text-(--color-accent) hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)',
          )}
        >
          Desfazer
        </button>
      </div>
      <div
        aria-hidden="true"
        className="h-1 bg-(--color-accent) transition-[width] duration-75"
        style={{ width: `${pct * 100}%` }}
      />
    </div>
  );
}
